import moment from "moment";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { AES, enc } from 'crypto-js';
import { ResizeObserver } from 'resize-observer';
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import DateConverter from "../../helpers/dateFormatChange.js";
import LeftArrow from "../../images/icons/blue-left-arrow.svg";
import crssrImage from "../../images/singleinvoice/cross-red.svg";
import axios from "axios";
import { setHeaderText } from "../../features/headerText/headerTextSlice.js";
import { useProjectList } from "../../features/projects/ProjectList.jsx";
import { ProjectBudget, ProjectCost, Roi, TrackedProjectHours, TrackedProjectHoursByProjectId } from "../../helpers/ProjectHelpers.jsx";
import NewProjectModal from "./helpers/NewProjectModal.jsx";
import { renderClientProjectCTA } from "../../helpers/invoiceHelpers.jsx";
import { useFetchClientProjectDetails } from "../../components/Clients/ClientInvoice/useClientInvoice.jsx";
import { DatePicker } from "rsuite";
import subDays from "date-fns/subDays";
import "./Project.css";
import CalendarIcon from '../../components/ui/CalendarIcon';
import { isMvpEnv } from "../../utils/brandLogo";
import MvpClientProjectList from "../Clients/mvpDetails/MvpClientProjectList";

const LegacyProject = () => {

    const { encryptedId } = useParams();
    const decodedId = decodeURIComponent(encryptedId.replace(/%2F/g, '/'));
    const decryptedBytes = AES.decrypt(decodedId, 'encryptionKey');
    const id = decryptedBytes.toString(enc.Utf8);
    const settings = JSON.parse(localStorage.getItem("settings"));
    const { forex } = useSelector((state) => state.forex);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [updatedProject, setUpdatedProject] = useState([]);
    const [filter, setFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectAllByPage, setSelectAllByPage] = useState({});
    const [showingDropdownOpen, setShowingDropdownOpen] = useState(false);
    const [userId, setUserId] = useState("");
    const targetRef = useRef(null);
    const showingDropdownRef = useRef(null);
    const { appSettings } = useSelector((state) => state.appSettings);
    const { projectAdded } = useSelector((state) => state.project);
    const statusDropdownRef = useRef(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // ── HubSpot company mapping ──────────────────────────────────────────────
    const [hubspotConnected, setHubspotConnected] = useState(false);
    const [hubspotCompanyId, setHubspotCompanyId] = useState(null);
    const [hubspotMappedCompanyId, setHubspotMappedCompanyId] = useState(null);
    const [hubspotMappedCompanyName, setHubspotMappedCompanyName] = useState(null);
    const [hubspotMapModalOpen, setHubspotMapModalOpen] = useState(false);
    const [hubspotCompanySearch, setHubspotCompanySearch] = useState("");
    const [hubspotCompanyResults, setHubspotCompanyResults] = useState([]);
    const [hubspotCompanySearchLoading, setHubspotCompanySearchLoading] = useState(false);
    const [hubspotSelectedCompany, setHubspotSelectedCompany] = useState(null);
    const [hubspotMapSaving, setHubspotMapSaving] = useState(false);
    const [hubspotDetailsModalOpen, setHubspotDetailsModalOpen] = useState(false);
    const hubspotSearchTimerRef = useRef(null);
    // Sync modal state
    const [syncModalOpen, setSyncModalOpen] = useState(false);
    const [hsProjects, setHsProjects] = useState([]);
    const [hsProjectsLoading, setHsProjectsLoading] = useState(false);
    // Push modal state
    const [pushProject, setPushProject] = useState(null);
    const [pushModalOpen, setPushModalOpen] = useState(false);
    const [pushForm, setPushForm] = useState({ project_name: "", start_date: "", end_date: "", budget: "", is_billable: true });
    const [needsForm, setNeedsForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [pushing, setPushing] = useState(false);

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
        setCurrentPage(0); // Reset page to the first page
    };

    let token = null;
    const { user } = useSelector((state) => state.auth);
    if (user) {
        if (user && user.token) {
            token = user.token;
        }
    }

    // Fetch HubSpot connection status
    useEffect(() => {
        if (!token) return;
        axios
            .get("/api/integrations/hubspot/config", { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setHubspotConnected(!!res.data?.connected))
            .catch(() => setHubspotConnected(false));
    }, [token]);

    // Sync client-level HubSpot mapping from client data
    useEffect(() => {
        if (!token || !id) return;
        axios
            .get(`/api/client-details/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => {
                const client = res.data;
                const cid = client?.hubspot_company_id || null;
                setHubspotMappedCompanyId(cid);
                setHubspotCompanyId(cid);
                setHubspotMappedCompanyName(client?.hubspot_company_name || null);
            })
            .catch(() => {});
    }, [token, id]);


    // ── HubSpot mapping handlers ─────────────────────────────────────────────
    const openHubspotMapModal = () => {
        setHubspotCompanySearch("");
        setHubspotCompanyResults([]);
        setHubspotSelectedCompany(null);
        setHubspotMapModalOpen(true);
    };

    const closeHubspotMapModal = () => {
        setHubspotMapModalOpen(false);
        setHubspotCompanySearch("");
        setHubspotCompanyResults([]);
        setHubspotSelectedCompany(null);
    };

    const searchHubspotCompanies = useCallback(async (q) => {
        if (!token) return;
        try {
            setHubspotCompanySearchLoading(true);
            const res = await axios.get(
                `/api/integrations/hubspot/companies/search?q=${encodeURIComponent(q)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHubspotCompanyResults(res.data?.companies || []);
        } catch (err) {
            toast.error("Failed to search HubSpot companies.", { position: "bottom-right", autoClose: 2000 });
        } finally {
            setHubspotCompanySearchLoading(false);
        }
    }, [token]);

    const handleHubspotCompanySearchChange = (e) => {
        const q = e.target.value;
        setHubspotCompanySearch(q);
        setHubspotSelectedCompany(null);
        clearTimeout(hubspotSearchTimerRef.current);
        hubspotSearchTimerRef.current = setTimeout(() => searchHubspotCompanies(q), 400);
    };

    const handleSaveHubspotMap = async () => {
        if (!hubspotSelectedCompany) return;
        try {
            setHubspotMapSaving(true);
            await axios.post(
                "/api/integrations/hubspot/client-map",
                { clientId: id, companyId: hubspotSelectedCompany.id, companyName: hubspotSelectedCompany.name, companyDomain: hubspotSelectedCompany.domain },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHubspotMappedCompanyId(hubspotSelectedCompany.id);
            setHubspotMappedCompanyName(hubspotSelectedCompany.name);
            toast.success(`Client mapped to "${hubspotSelectedCompany.name}" in HubSpot.`, { position: "bottom-right", autoClose: 2000 });
            closeHubspotMapModal();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save mapping.", { position: "bottom-right", autoClose: 2000 });
        } finally {
            setHubspotMapSaving(false);
        }
    };

    const handleRemoveHubspotMap = async () => {
        try {
            await axios.delete(`/api/integrations/hubspot/client-map/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            setHubspotMappedCompanyId(null);
            setHubspotMappedCompanyName(null);
            setHubspotDetailsModalOpen(false);
            toast.success("HubSpot mapping removed.", { position: "bottom-right", autoClose: 2000 });
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to remove mapping.", { position: "bottom-right", autoClose: 2000 });
        }
    };

    const handleRemapClick = () => {
        setHubspotDetailsModalOpen(false);
        openHubspotMapModal();
    };

    // ── Sync from HubSpot handlers ───────────────────────────────────────────
    const openSyncModal = useCallback(async () => {
        if (!hubspotCompanyId) return;
        setSyncModalOpen(true);
        setHsProjectsLoading(true);
        try {
            const res = await axios.get(
                `/api/integrations/hubspot/company/${hubspotCompanyId}/projects`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHsProjects(res.data.projects || []);
        } catch (err) {
            toast.error("Failed to load HubSpot projects.", { position: "bottom-right", autoClose: 2000 });
        } finally {
            setHsProjectsLoading(false);
        }
    }, [hubspotCompanyId, token]);

    const openPushModal = (hsProject) => {
        const missing = !hsProject.name;
        setPushProject(hsProject);
        setNeedsForm(missing);
        setEditMode(false);
        setConfirmed(false);
        setPushForm({
            project_name: hsProject.name || "",
            start_date: hsProject.start_date ? hsProject.start_date.slice(0, 10) : "",
            end_date: hsProject.end_date ? hsProject.end_date.slice(0, 10) : "",
            budget: "",
            is_billable: true,
        });
        setPushModalOpen(true);
    };

    const handlePushSubmit = async () => {
        if (!confirmed && !needsForm) {
            toast.warn("Please confirm before pushing.", { position: "bottom-right", autoClose: 2000 });
            return;
        }
        setPushing(true);
        try {
            const res = await axios.post(
                `/api/integrations/hubspot/projects/${pushProject.id}/push`,
                {
                    clientId: id,
                    project_name: pushForm.project_name,
                    start_date: pushForm.start_date || null,
                    end_date: pushForm.end_date || null,
                    budget: pushForm.budget || 0,
                    is_billable: pushForm.is_billable,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.status === "already_synced") {
                toast.info("This project is already synced to Talenton.", { position: "bottom-right", autoClose: 3000 });
            } else {
                toast.success(`Project "${pushForm.project_name}" pushed to Talenton!`, { position: "bottom-right", autoClose: 3000 });
                setHsProjects(prev =>
                    prev.map(p => p.id === pushProject.id
                        ? { ...p, synced: true, talenton_project_name: pushForm.project_name }
                        : p
                    )
                );
                refetchProjects();
            }
            setPushModalOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to push project.", { position: "bottom-right", autoClose: 3000 });
        } finally {
            setPushing(false);
        }
    };
    const renderProjectBillableStatus = (rowData) => {
        // Determine the class based on the payment_status value
        let statusClass;
        let statusName;
        if (rowData.is_billable === true) {
            statusClass = "active";
            statusName = "Yes";
        } else if (rowData.is_billable === false) {
            statusClass = "inactive";
            statusName = "No";
        }
        return (
            <span className={`payment-status ${statusClass}`}>{statusName}</span>
        );
    };

    const renderProjectStatus = (rowData) => {
        let statusClass;
        let statusName;
        if (rowData.is_active === true) {
            statusClass = "active";
            statusName = "Active";
        } else if (rowData.is_active === false) {
            statusClass = "inactive";
            statusName = "Inactive";
        }
        return (
            <span className={`payment-status ${statusClass}`}>{statusName}</span>
        );
    };

    const renderProjectArchiveStatus = (rowData) => {
        let statusClass;
        let statusName;
        if (rowData.is_archived === true) {
            statusClass = "active";
            statusName = "Archived";
        } else if (rowData.is_archived === false) {
            statusClass = "inactive";
            statusName = "Archived";
        }
        return (
            <span className={`payment-status ${statusClass} archived`}>{statusName}</span>
        );
    };

    // react Query for project
    const { data: project = [], isLoading, refetch: refetchProjects } = useFetchClientProjectDetails(id, token);

    useEffect(() => {
        const initializeObserver = () => {
            const resizeObserver = new ResizeObserver((entries) => {
                // Your resize observer logic here
                setTimeout(() => {
                    entries.forEach((entry) => {
                        // console.log("Element resized:", entry.target);
                        // Perform non-blocking operations as necessary
                    });
                }, 0);
            });

            if (targetRef.current) {
                resizeObserver.observe(targetRef.current);
            }

            // Clean up the observer on component unmount
            return () => {
                setTimeout(() => {
                    if (targetRef.current) {
                        resizeObserver.unobserve(targetRef.current);
                    }
                }, 0);
            };
        };

        // Defer the initialization
        setTimeout(initializeObserver, 0);
    }, []);



    useEffect(() => {
        const initialize = async () => {
            // Set the updated project
            setUpdatedProject(project);

            // Check user type and set user ID
            if (user.userType !== 1 && user.userType !== 2) {
                setUserId(user._id);
            }

            // Refetch projects in a non-blocking manner
            try {
                await Promise.resolve(refetchProjects());
            } catch (error) {
                console.error("Error refetching projects:", error);
            }
        };

        // Defer initialization to ensure non-blocking
        setTimeout(initialize, 0);
    }, [dispatch, project, token, user._id, user.userType, projectAdded]);

    const handleClickOutsideDropdown = (event) => {
        const isClickInsideShowingDropdown = showingDropdownRef.current && showingDropdownRef.current.contains(event.target);
        if (!isClickInsideShowingDropdown) {
            setShowingDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutsideDropdown);
        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
        };
    }, []);

    var filteredData = updatedProject.filter((item) => {
        const searchTerm = filter.trim().toLowerCase();
        const hasMatch = item.project_name.toLowerCase().includes(searchTerm);
        return hasMatch;
    });

    useEffect(() => {

        if (selectedDate) {
            console.log('selectedDate', selectedDate);
            const filteredData = updatedProject.filter((item) => {
                return moment.tz(item.created_at, global.config.getTimeZone()).format('YYYY-MM-DD') === moment.tz(selectedDate, global.config.getTimeZone()).format('YYYY-MM-DD');
            });
            setUpdatedProject(filteredData);
        }
        // console.log('selectedStatus', selectedStatus);

        if (selectedStatus === "Active") {
            // console.log('project', project);
            setUpdatedProject(project.filter((item) => item.is_active === true))
        } else if (selectedStatus === "Inactive") {
            setUpdatedProject(project.filter((item) => item.is_active === false))
        } else if (selectedStatus === "Archived") {
            setUpdatedProject(project.filter((item) => item.is_archived === true))
        } else if (selectedStatus === "All") {
            setUpdatedProject(project);
        }

        if (selectedDate && selectedStatus) {

            if (selectedStatus === "Active") {

                console.log('project', project);

                setUpdatedProject(project.filter((item) => item.is_active === true && moment.tz(item.created_at, global.config.getTimeZone()).format('YYYY-MM-DD') === moment.tz(selectedDate, global.config.getTimeZone()).format('YYYY-MM-DD')))

            } else if (selectedStatus === "Inactive") {
                setUpdatedProject(project.filter((item) => item.is_active === false && moment.tz(item.created_at, global.config.getTimeZone()).format('YYYY-MM-DD') === moment.tz(selectedDate, global.config.getTimeZone()).format('YYYY-MM-DD')))
            } else if (selectedStatus === "Archived") {

                setUpdatedProject(project.filter((item) => item.is_archived === true && moment.tz(item.created_at, global.config.getTimeZone()).format('YYYY-MM-DD') === moment.tz(selectedDate, global.config.getTimeZone()).format('YYYY-MM-DD')))

            } else if (selectedStatus === "All") {

                setUpdatedProject(project.filter((item) => moment.tz(item.created_at, global.config.getTimeZone()).format('YYYY-MM-DD') === moment.tz(selectedDate, global.config.getTimeZone()).format('YYYY-MM-DD')))
            }



        }

    }, [selectedDate, selectedStatus]);

    const totalResults = filteredData.length;


    const handleRowsPerPageChange = (e) => {
        const newRowsPerPage = parseInt(e.target.value);
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(0); // Set the current page to 0 when changing rows per page
    };

    const onPageChange = (e) => {
        const newPage = e.page;
        // Store the current "Select All" state for the current page
        setSelectAllByPage((prevSelectAllByPage) => ({
            ...prevSelectAllByPage,
        }));
        setCurrentPage(e.page);
    };


    function ConvertHelper(currencyFrom, currencyTo, amount, forex) {
        const currencyFromRate = forex[currencyFrom];
        const currencyToRate = forex[currencyTo];
        const amountConvertedUSD = amount / currencyFromRate;
        const amountConvertTo = amountConvertedUSD * currencyToRate;
        return amountConvertTo;
    }


    const toggleStatusDropdown = () => {
        setStatusDropdownOpen(!statusDropdownOpen);
    };

    const selectStatusOption = (selectedStatus) => {
        setSelectedStatus(selectedStatus);
        setStatusDropdownOpen(false);
    };

    const ranges = [
        {
            label: "today",
            value: new Date(), // Use a single date instead of an array
        },
        {
            label: "yesterday",
            value: subDays(new Date(), 1),
        },
    ];

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setCurrentPage(1);
    };

    const handleApplyConfirm = () => {
        setShowConfirmationModal(false);
        setShowFilterModal(false);
    };

    const toggleFilterModal = () => {
        setShowConfirmationModal(false);
        setShowFilterModal(!showFilterModal);
    };
    const handleCancel = () => {
        setShowConfirmationModal(false);
    };

    const applyFilters = () => {
        setShowFilterModal(false);
    };

    const clearFilters = () => {

        setShowFilterModal(false);
    };

    const checkAndShowConfirmationModal = () => {
        // const isTeamsModified = !isEqual(
        //   selectedTeams,
        //   userProfiles.map((team) => team._id)
        // );
        // const isUserCategoryModified = !isEqual(
        //   selectedStatus,
        //   userCategoryList.map((item) => item.value)
        // );
        // const isAccountTypeModified = !isEqual(
        //   selectedAccountType,
        //   filteredRoleId.map((type) => type._id)
        // );

        // if (isTeamsModified || isUserCategoryModified || isAccountTypeModified) {
        //   setShowConfirmationModal(true);
        // } else {
        //   setShowFilterModal(false);
        // }
    };

    return (
        <div className="outer-project-client-section">
            <div className="text-arrow-outer">
                <div className="text-arrow-inner">
                    <Link to={`/client/details/${encodeURIComponent(AES.encrypt(id, 'encryptionKey').toString().replace(/\//g, '%2F'))}`}>
                        <div className="text-arrow">
                            <div className="left-arrow">
                                <img src={LeftArrow} alt="left-arrow" />
                            </div>
                            <div className="top-text">
                                Back to Overview
                            </div>
                        </div>
                    </Link>
                </div>
            </div>

            <div className="user-outer-overview invoice-outer-invoice"  >
                <div className="invoice-top">
                    <div className="new-top-flter">

                        <div className="project-action-btns">
                            <NewProjectModal clientId={id} />

                            {/* Sync from HubSpot button */}
                            {hubspotConnected && hubspotCompanyId && (
                                <button className="hs-sync-project-btn" onClick={openSyncModal}>
                                    <svg width="16" height="16" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M426.6 241.3c-18.7-26.5-45.7-43.2-76.2-48.1v-54.7c13.5-5.8 22.9-19.2 22.9-34.8V92.3C373.3 69 354.3 50 331 50h-11.4c-23.3 0-42.3 19-42.3 42.3v11.4c0 15.6 9.4 29 22.9 34.8v54.7c-22.4 3.4-43.5 12.8-61.5 27.7L117.2 141c1-3.7 1.6-7.6 1.6-11.6 0-25.3-20.5-45.8-45.8-45.8S27.2 104.1 27.2 129.4s20.5 45.8 45.8 45.8c10.6 0 20.4-3.6 28.1-9.6l119.1 78.3c-11.2 20.4-17.4 43.7-17.4 67.8 0 28.3 8.4 55.2 23.9 78l-36.2 36.2c-2.8-.9-5.7-1.4-8.8-1.4-16.5 0-29.9 13.4-29.9 29.9S165.2 484 181.7 484s29.9-13.4 29.9-29.9c0-3.1-.5-6.1-1.4-8.8l35.8-35.8c23.3 16.3 51.1 25.5 80.6 25.5 78.7 0 142.6-63.9 142.6-142.6 0-21.2-4.7-41.7-13.4-60.1l-8.2 8.6-.9-1.6zM325.6 392.5c-55.8 0-101.1-45.3-101.1-101.1s45.3-101.1 101.1-101.1 101.1 45.3 101.1 101.1-45.3 101.1-101.1 101.1z" fill="#ffffff"/>
                                    </svg>
                                    Sync from HubSpot
                                </button>
                            )}
                        </div>

                        <div className="search-sec">
                            <div className="search-container">
                                <input
                                    type="text"
                                    onChange={handleFilterChange}
                                    value={filter}
                                    className="search-input"
                                    placeholder="Search by Project name"
                                />
                            </div>
                        </div>

                    </div>
                    <div className="invoice-filter">
                        <div className="invoice-filter-inner-section no-flter-all">
                            <div className="filter-inner invoice-type">
                                <label htmlFor="invoice-type">Project Status</label>
                                <div className="custom-dropdown1" ref={statusDropdownRef}>
                                    <div className="drop-down-inner">
                                        <div
                                            className="selected-options1"
                                            onClick={toggleStatusDropdown}
                                        >
                                            {selectedStatus ? selectedStatus : "All"}
                                        </div>
                                        {statusDropdownOpen && (
                                            <div className="custom-dropdown-new">
                                                <ul className="type-list">
                                                    <li onClick={() => selectStatusOption('All')}>All</li>
                                                    <li onClick={() => selectStatusOption('Active')}>{global.config.locate(appSettings && appSettings.language, 'Active')}</li>
                                                    <li onClick={() => selectStatusOption('Inactive')}>{global.config.locate(appSettings && appSettings.language, 'Inactive')}</li>
                                                    <li onClick={() => selectStatusOption('Archived')}>Archived</li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="filter-inner due-date">
                                <label htmlFor="dueDate" className="calendar-icon">
                                    Date Created
                                </label>
                                <div className="date-picker-outer date-picker-outer-all">
                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                        <DatePicker
                                            oneTap // Use DatePicker instead of DateRangePicker
                                            format="dd/MM/yyyy"
                                            ranges={ranges}
                                            value={selectedDate}
                                            onChange={handleDateChange}
                                            placement="auto"
                                            className="invoice-filter-top-date"
                                            isoWeek={appSettings.weekStartDay && appSettings.weekStartDay === 'Monday'}
              caretAs={CalendarIcon}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="filter-invoice-right apply-fliter-right">
                            <button onClick={toggleFilterModal}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="13"
                                    height="17"
                                    viewBox="0 0 13 17"
                                    fill="none"
                                >
                                    <path
                                        d="M11.7363 8.81108C11.4276 9.81451 10.4925 10.5444 9.38889 10.5444C8.28471 10.5444 7.35016 9.8141 7.04147 8.81108L0.722221 8.81108C0.323349 8.81108 -1.07845e-06 8.48773 -1.06101e-06 8.08886C-1.04357e-06 7.68998 0.323349 7.36663 0.722221 7.36663L7.04144 7.36663C7.35022 6.3632 8.28529 5.6333 9.38889 5.6333C10.4931 5.6333 11.4276 6.36362 11.7363 7.36663L12.2778 7.36663C12.6767 7.36663 13 7.68998 13 8.08886C13 8.48773 12.6766 8.81108 12.2778 8.81108L11.7363 8.81108ZM9.38889 7.07775C8.83162 7.07775 8.37778 7.53123 8.37778 8.08886C8.37778 8.64613 8.83127 9.09997 9.38889 9.09997C9.94616 9.09997 10.4 8.64648 10.4 8.08886C10.4 7.53158 9.94651 7.07775 9.38889 7.07775Z"
                                        fill="#6479F8"
                                    />
                                    <path
                                        d="M5.38078 3.17778C5.072 4.18121 4.13693 4.91111 3.03333 4.91111C1.92882 4.91111 0.994035 4.18035 0.68563 3.17687C0.303759 3.15781 -1.07791e-06 2.84216 -1.06101e-06 2.45556C-1.04411e-06 2.06896 0.303748 1.75331 0.685606 1.73425C0.994098 0.730343 1.9294 -4.83911e-07 3.03333 -4.35657e-07C4.13752 -3.87392e-07 5.07206 0.730315 5.38076 1.73333L12.2778 1.73333C12.6767 1.73333 13 2.05668 13 2.45556C13 2.85443 12.6766 3.17778 12.2778 3.17778L5.38078 3.17778ZM3.03333 1.44444C2.47606 1.44444 2.02222 1.89793 2.02222 2.45556C2.02222 3.01283 2.47571 3.46667 3.03333 3.46667C3.5906 3.46667 4.04444 3.01318 4.04444 2.45556C4.04444 1.89828 3.59096 1.44444 3.03333 1.44444Z"
                                        fill="#6479F8"
                                    />
                                    <path
                                        d="M7.403 14.4444C7.09422 15.4478 6.15916 16.1777 5.05556 16.1777C3.95137 16.1777 3.01682 15.4474 2.70813 14.4444L0.722221 14.4444C0.323349 14.4444 -1.07845e-06 14.121 -1.06101e-06 13.7222C-1.04357e-06 13.3233 0.323349 12.9999 0.722221 12.9999L2.70811 12.9999C3.01689 11.9965 3.95195 11.2666 5.05556 11.2666C6.15974 11.2666 7.09429 11.9969 7.40298 12.9999L12.2778 12.9999C12.6767 12.9999 13 13.3233 13 13.7222C13 14.121 12.6766 14.4444 12.2778 14.4444L7.403 14.4444ZM5.05556 12.711C4.49828 12.711 4.04444 13.1645 4.04444 13.7222C4.04444 14.2794 4.49793 14.7333 5.05556 14.7333C5.61283 14.7333 6.06667 14.2798 6.06667 13.7222C6.06667 13.1649 5.61318 12.711 5.05556 12.711Z"
                                        fill="#6479F8"
                                    />
                                </svg>
                                All Filters
                            </button>
                        </div>
                        <Modal
                            isOpen={showFilterModal}
                            onRequestClose={checkAndShowConfirmationModal}
                            contentLabel="Filter Modal"
                            className="new-filter-modal"
                            style={{
                                content: {
                                    height: "50%",
                                    width: "50%",
                                    position: "fixed",
                                    top: "50%",
                                    left: "50%",
                                    borderRadius: "10px",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    backgroundColor: "#fff",
                                    padding: "20px",
                                    // overflow: "auto",
                                },
                                overlay: {
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                },
                            }}
                        >
                            <div className="new-nodal-invoice-outer-sec">

                                <div className="new-modal-top-sec">
                                    <div className="new-modal-heading">
                                        All filters
                                    </div>
                                    <a className="close-image-clr1" onClick={checkAndShowConfirmationModal}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M1 13L13 1" stroke="black" />
                                            <path d="M1 0.999999L13 13" stroke="black" />
                                        </svg>{" "}
                                    </a>
                                </div>
                                <div className="invoice-filter-inner-section">

                                    <div className="filter-inner invoice-type">
                                        <label htmlFor="invoice-type">Project Status</label>
                                        <div className="custom-dropdown1" ref={statusDropdownRef}>
                                            <div className="drop-down-inner">
                                                <div
                                                    className="selected-options1"
                                                    onClick={toggleStatusDropdown}
                                                >
                                                    {selectedStatus ? global.config.locate(appSettings && appSettings.language, selectedStatus) : (appSettings && appSettings.language ? global.config.locate(appSettings.language, 'ALL') : null)}
                                                </div>
                                                {statusDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="type-list">
                                                            <li onClick={() => selectStatusOption(null)}>All</li>
                                                            <li onClick={() => selectStatusOption('Active')}>{global.config.locate(appSettings && appSettings.language, 'Active')}</li>
                                                            <li onClick={() => selectStatusOption('Inactive')}>{global.config.locate(appSettings && appSettings.language, 'Inactive')}</li>
                                                            <li onClick={() => selectStatusOption('Archived')}>Archived</li>
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="filter-inner due-date">
                                        <label htmlFor="dueDate" className="calendar-icon">
                                            Date Created
                                        </label>
                                        <div className="date-picker-outer date-picker-outer-all">
                                            <div className="custoom-icon-calender custoom-icon-calender-all">
                                                <DatePicker
                                                    oneTap // Use DatePicker instead of DateRangePicker
                                                    format="dd/MM/yyyy"
                                                    ranges={ranges}
                                                    value={selectedDate}
                                                    onChange={handleDateChange}
                                                    placement="auto"
                                                    className="invoice-filter-top-date"
                                                    isoWeek={appSettings.weekStartDay && appSettings.weekStartDay === 'Monday'}
              caretAs={CalendarIcon}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <Modal
                                        className="delete-modal"
                                        isOpen={showConfirmationModal}
                                        onRequestClose={handleCancel}
                                        contentLabel="Confirm Delete"
                                        style={{
                                            content: {
                                                height: "41%",
                                                width: "30%",
                                                position: "fixed",
                                                top: "36%",
                                                left: "50%",
                                            },
                                        }}
                                    >
                                        <div className="delete-modal-content">
                                            <h2>Apply or Discard changes?</h2>
                                            <p>
                                                Do you want to Apply or Discard filter changes?
                                            </p>

                                            <div className="delete-modal-buttons">
                                                <button className="delete" onClick={handleApplyConfirm} >
                                                    Apply Changes
                                                </button>
                                                <button onClick={clearFilters}>
                                                    Discard Changes
                                                </button>
                                            </div>
                                        </div>
                                    </Modal>
                                </div>
                            </div>
                            <div className="filter-buttons">
                                <button className="clear-all" onClick={clearFilters}>Clear All</button>
                                <button className="apply-filter" onClick={applyFilters}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="17" viewBox="0 0 13 17" fill="none">
                                        <path d="M11.7363 8.81108C11.4276 9.81451 10.4925 10.5444 9.38889 10.5444C8.28471 10.5444 7.35016 9.8141 7.04147 8.81108L0.722221 8.81108C0.323349 8.81108 -1.07845e-06 8.48773 -1.06101e-06 8.08886C-1.04357e-06 7.68998 0.323349 7.36663 0.722221 7.36663L7.04144 7.36663C7.35022 6.3632 8.28529 5.6333 9.38889 5.6333C10.4931 5.6333 11.4276 6.36362 11.7363 7.36663L12.2778 7.36663C12.6767 7.36663 13 7.68998 13 8.08886C13 8.48773 12.6766 8.81108 12.2778 8.81108L11.7363 8.81108ZM9.38889 7.07775C8.83162 7.07775 8.37778 7.53123 8.37778 8.08886C8.37778 8.64613 8.83127 9.09997 9.38889 9.09997C9.94616 9.09997 10.4 8.64648 10.4 8.08886C10.4 7.53158 9.94651 7.07775 9.38889 7.07775Z" fill="white" />
                                        <path d="M5.38078 3.17778C5.072 4.18121 4.13693 4.91111 3.03333 4.91111C1.92882 4.91111 0.994035 4.18035 0.68563 3.17687C0.303759 3.15781 -1.07791e-06 2.84216 -1.06101e-06 2.45556C-1.04411e-06 2.06896 0.303748 1.75331 0.685606 1.73425C0.994098 0.730343 1.9294 -4.83911e-07 3.03333 -4.35657e-07C4.13752 -3.87392e-07 5.07206 0.730315 5.38076 1.73333L12.2778 1.73333C12.6767 1.73333 13 2.05668 13 2.45556C13 2.85443 12.6766 3.17778 12.2778 3.17778L5.38078 3.17778ZM3.03333 1.44444C2.47606 1.44444 2.02222 1.89793 2.02222 2.45556C2.02222 3.01283 2.47571 3.46667 3.03333 3.46667C3.5906 3.46667 4.04444 3.01318 4.04444 2.45556C4.04444 1.89828 3.59096 1.44444 3.03333 1.44444Z" fill="white" />
                                        <path d="M7.403 14.4444C7.09422 15.4478 6.15916 16.1777 5.05556 16.1777C3.95137 16.1777 3.01682 15.4474 2.70813 14.4444L0.722221 14.4444C0.323349 14.4444 -1.07845e-06 14.121 -1.06101e-06 13.7222C-1.04357e-06 13.3233 0.323349 12.9999 0.722221 12.9999L2.70811 12.9999C3.01689 11.9965 3.95195 11.2666 5.05556 11.2666C6.15974 11.2666 7.09429 11.9969 7.40298 12.9999L12.2778 12.9999C12.6767 12.9999 13 13.3233 13 13.7222C13 14.121 12.6766 14.4444 12.2778 14.4444L7.403 14.4444ZM5.05556 12.711C4.49828 12.711 4.04444 13.1645 4.04444 13.7222C4.04444 14.2794 4.49793 14.7333 5.05556 14.7333C5.61283 14.7333 6.06667 14.2798 6.06667 13.7222C6.06667 13.1649 5.61318 12.711 5.05556 12.711Z" fill="white" />
                                    </svg>
                                    Apply Filters
                                </button>
                            </div>
                        </Modal>
                    </div>
                    <div className="filter-invoice-right" ref={targetRef} >
                        <div className="filter-inner showing-result">
                            <label>Showing</label>

                            <div ref={showingDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                                <div className="selected-option custom-dropdown-user-new" onClick={() => setShowingDropdownOpen(!showingDropdownOpen)}>
                                    {totalResults < 10 ? totalResults.toString() : rowsPerPage.toString()}
                                </div>
                                {showingDropdownOpen && (
                                    <div className="custom-dropdown-new">
                                        <ul className="showing-options-list">
                                            {totalResults >= 10 && <li className={rowsPerPage === 10 ? 'selected-item' : ''} onClick={() => { handleRowsPerPageChange({ target: { value: 10 } }); setShowingDropdownOpen(false); }}>10</li>}
                                            {totalResults >= 20 && <li className={rowsPerPage === 20 ? 'selected-item' : ''} onClick={() => { handleRowsPerPageChange({ target: { value: 20 } }); setShowingDropdownOpen(false); }}>20</li>}
                                            {totalResults >= 30 && <li className={rowsPerPage === 30 ? 'selected-item' : ''} onClick={() => { handleRowsPerPageChange({ target: { value: 30 } }); setShowingDropdownOpen(false); }}>30</li>}
                                            {totalResults >= 40 && <li className={rowsPerPage === 40 ? 'selected-item' : ''} onClick={() => { handleRowsPerPageChange({ target: { value: 40 } }); setShowingDropdownOpen(false); }}>40</li>}
                                            {totalResults >= 10 && <li className={totalResults >= 10 && rowsPerPage === totalResults ? 'selected-item' : ''} onClick={() => { handleRowsPerPageChange({ target: { value: totalResults }, }); setShowingDropdownOpen(false); }}>ALL</li>}
                                            {/* Add more conditions as needed */}
                                            {totalResults < 10 ? (
                                                <li className={totalResults < 10 ? 'selected-item' : ''} onClick={() => { handleRowsPerPageChange({ target: { value: totalResults }, }); setShowingDropdownOpen(false); }}>{totalResults}</li>
                                            ) : null}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <label>of {totalResults} Results</label>
                        </div>
                    </div>
                </div>

                <div className="datatable-team-maintb">
                    <style>
                        {`
                          @keyframes pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.5; }
                          }
                        `}
                    </style>
                    {isLoading ? (
                        <DataTable
                            value={Array.from({ length: rowsPerPage })}
                            removableSort
                            tableStyle={{
                                minWidth: "50rem",
                                width: "100%",
                                textAlign: "left",
                            }}
                            paginator={false}
                            paginatorClassName="recent-invoices-pagination"
                            rows={rowsPerPage}
                        >
                            <Column
                                header="Project"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "180px",
                                            height: "22px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="Date Created"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "120px",
                                            height: "22px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="Tracked Hours"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "90px",
                                            height: "22px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="Budget"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "100px",
                                            height: "22px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="Cost"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "100px",
                                            height: "22px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="ROI"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "70px",
                                            height: "22px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="Billable"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "50px",
                                            height: "24px",
                                            borderRadius: "999px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                header="Status"
                                sortable
                                style={{ paddingRight: "44px" }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "70px",
                                            height: "24px",
                                            borderRadius: "999px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                            <Column
                                field="_id"
                                style={{
                                    paddingRight: "45px",
                                    minWidth: "121.73px",
                                }}
                                body={() => (
                                    <div
                                        style={{
                                            width: "86px",
                                            height: "30px",
                                            borderRadius: "8px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                            />
                        </DataTable>
                    ) : (
                        <DataTable
                            value={filteredData}
                            removableSort
                            tableStyle={{
                                minWidth: "50rem",
                                width: "100%",
                                textAlign: "left",
                            }}
                            paginator={filteredData.length > 10}
                            paginatorClassName="recent-invoices-pagination"
                            rows={rowsPerPage} // Set the number of rows per page
                            first={currentPage * rowsPerPage} // Set the current page using currentPage
                            onPage={onPageChange} // Update currentPage when changing pages
                        >
                            <Column
                                field="project_name"
                                header="Project"
                                className=""
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="created_at"
                                body={(rowData) => (
                                    <DateConverter
                                        date={rowData.created_at}
                                    />
                                )}
                                header="Date Created"
                                className=""
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="tracked_hours"
                                header="Tracked Hours"
                                className=""
                                body={TrackedProjectHoursByProjectId}
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="budget"
                                header="Budget"
                                // body={(rowData) => {
                                //     const { client_name, budget } = rowData;
                                //     if (client_name && client_name.preferred_currency) {
                                //         return new Intl.NumberFormat("en-US", {
                                //             style: "currency",
                                //             currency: settings.currency,
                                //         }).format(ConvertHelper(client_name.preferred_currency, settings.currency, budget, forex));
                                //     } else {
                                //         return new Intl.NumberFormat("en-US", {
                                //             style: "currency",
                                //             currency: settings.currency,
                                //         }).format(ConvertHelper("USD", settings.currency, budget, forex));
                                //     }
                                // }}
                                body={ProjectBudget}
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="cost"
                                header="Cost"
                                body={ProjectCost}
                                className=""
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="roi"
                                header="ROI"
                                body={Roi}
                                className=""
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="is_billable"
                                header="Billable"
                                body={(rowData) => renderProjectBillableStatus(rowData)}
                                className="amount-bold"
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            {/* <Column
                                field="is_archived"
                                header="Archived"
                                body={(rowData) => renderProjectArchiveStatus(rowData)}
                                className="amount-bold"
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column> */}
                            <Column
                                field="is_active"
                                header="Status"
                                body={(rowData) => rowData.is_archived ? renderProjectArchiveStatus(rowData) : renderProjectStatus(rowData)}
                                className="amount-bold"
                                sortable
                                style={{ paddingRight: "44px" }}
                            ></Column>
                            <Column
                                field="_id"
                                body={renderClientProjectCTA}
                                className=""
                                style={{
                                    paddingRight: "45px",
                                    minWidth: "121.73px",
                                }}
                            ></Column>
                        </DataTable>
                    )}
                </div>
            </div>

            {/* ── HubSpot Map Company Modal ──────────────────────────────────────── */}
            <Modal
                className="client-modal"
                isOpen={hubspotMapModalOpen}
                onRequestClose={closeHubspotMapModal}
                contentLabel="Map to HubSpot Company"
                style={{
                    content: {
                        width: "30%",
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        parent: document.querySelector(".admin-outer.time.tracker"),
                    },
                }}
            >
                <div className="modal">
                    <div className="modal-close-invite outter-modal-main">
                        <a onClick={closeHubspotMapModal} style={{ cursor: "pointer" }}>
                            <img src={crssrImage} alt="Close" />
                        </a>
                    </div>
                    <div className="modal-client-head">
                        <h2>Map to HubSpot Company</h2>
                    </div>
                    <div className="form-field-client-outer">
                        <div className="form-field-client" style={{ marginBottom: "12px" }}>
                            <label className="form-label">Search HubSpot Companies</label>
                            <input
                                type="text"
                                className="form-input"
                                autoFocus
                                placeholder="Search by name or domain..."
                                value={hubspotCompanySearch}
                                onChange={handleHubspotCompanySearchChange}
                                onFocus={() => { if (!hubspotCompanyResults.length && !hubspotCompanySearch) searchHubspotCompanies(""); }}
                            />
                        </div>
                        <div style={{ maxHeight: "260px", overflowY: "auto", border: "1px solid #e8e8e8", borderRadius: "6px", marginBottom: "16px" }}>
                            {hubspotCompanySearchLoading ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>Searching...</div>
                            ) : hubspotCompanyResults.length === 0 ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>
                                    {hubspotCompanySearch ? "No companies found." : "Type to search HubSpot companies."}
                                </div>
                            ) : (
                                hubspotCompanyResults.map((company, idx) => {
                                    const isSelected = hubspotSelectedCompany?.id === company.id;
                                    return (
                                        <div
                                            key={company.id}
                                            onClick={() => setHubspotSelectedCompany(isSelected ? null : company)}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "10px",
                                                padding: "10px 14px",
                                                cursor: "pointer",
                                                backgroundColor: isSelected ? "#fff5f2" : idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                                                borderBottom: idx < hubspotCompanyResults.length - 1 ? "1px solid #f3f4f6" : "none",
                                                borderLeft: isSelected ? "3px solid #ff7a59" : "3px solid transparent",
                                            }}
                                        >
                                            <div style={{ width: "30px", height: "30px", borderRadius: "6px", flexShrink: 0, background: isSelected ? "#ff7a59" : "#e8e8e8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, color: isSelected ? "#fff" : "#6b7280" }}>
                                                {(company.name || "?")[0].toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: "13px", fontWeight: isSelected ? 600 : 400, color: "#282828", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{company.name}</div>
                                                <div style={{ fontSize: "11px", color: "#96a4b2", marginTop: "2px" }}>
                                                    {company.domain || company.website || "No domain"}{company.city ? ` · ${company.city}` : ""}
                                                </div>
                                            </div>
                                            {isSelected && <span style={{ fontSize: "14px", color: "#ff7a59", flexShrink: 0, fontWeight: 700 }}>✓</span>}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        {hubspotSelectedCompany && (
                            <div style={{ marginBottom: "14px", fontSize: "12px", color: "#6b7280" }}>
                                Selected: <strong style={{ color: "#282828" }}>{hubspotSelectedCompany.name}</strong>
                            </div>
                        )}
                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                onClick={closeHubspotMapModal}
                                style={{ padding: "9px 18px", borderRadius: "6px", border: "1px solid #e8e8e8", background: "#fff", color: "#282828", fontSize: "13px", cursor: "pointer", fontWeight: 500 }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveHubspotMap}
                                disabled={!hubspotSelectedCompany || hubspotMapSaving}
                                style={{
                                    padding: "9px 18px",
                                    borderRadius: "6px",
                                    border: "none",
                                    background: hubspotSelectedCompany && !hubspotMapSaving ? "#405BFF" : "#e8e8e8",
                                    color: hubspotSelectedCompany && !hubspotMapSaving ? "#fff" : "#9ca3af",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: hubspotSelectedCompany && !hubspotMapSaving ? "pointer" : "not-allowed",
                                }}
                            >
                                {hubspotMapSaving ? "Saving..." : "Map Company"}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── HubSpot Mapped Details Modal ───────────────────────────────────── */}
            <Modal
                className="client-modal"
                isOpen={hubspotDetailsModalOpen}
                onRequestClose={() => setHubspotDetailsModalOpen(false)}
                contentLabel="HubSpot Company Details"
                style={{
                    content: {
                        width: "30%",
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        parent: document.querySelector(".admin-outer.time.tracker"),
                    },
                }}
            >
                <div className="modal">
                    <div className="modal-close-invite outter-modal-main">
                        <a onClick={() => setHubspotDetailsModalOpen(false)} style={{ cursor: "pointer" }}>
                            <img src={crssrImage} alt="Close" />
                        </a>
                    </div>
                    <div className="modal-client-head">
                        <h2>HubSpot Company</h2>
                    </div>
                    <div className="form-field-client-outer">
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px", backgroundColor: "#f9fafb", border: "1px solid #e8e8e8", borderRadius: "8px", marginBottom: "16px" }}>
                            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#fff5f2", border: "1px solid #ffb8a0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: 700, color: "#ff7a59", flexShrink: 0 }}>
                                {(hubspotMappedCompanyName || "?")[0]?.toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "#282828" }}>{hubspotMappedCompanyName || "HubSpot Company"}</div>
                                <div style={{ fontSize: "11px", color: "#96a4b2", marginTop: "3px" }}>HubSpot ID: {hubspotMappedCompanyId}</div>
                            </div>
                        </div>
                        <div style={{ padding: "12px 14px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", fontSize: "13px", color: "#15803d", marginBottom: "20px", lineHeight: "1.5" }}>
                            This client is linked to a HubSpot company. Projects created from HubSpot will automatically be associated with this client.
                        </div>
                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                onClick={handleRemoveHubspotMap}
                                style={{ padding: "9px 18px", borderRadius: "6px", border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                            >
                                Remove Mapping
                            </button>
                            <button
                                type="button"
                                onClick={handleRemapClick}
                                style={{ padding: "9px 18px", borderRadius: "6px", border: "none", background: "#405BFF", color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
                            >
                                Remap Company
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── HubSpot Sync Modal ─────────────────────────────────────────────── */}
            <Modal
                className="client-modal"
                isOpen={syncModalOpen}
                onRequestClose={() => setSyncModalOpen(false)}
                contentLabel="HubSpot Projects"
                style={{ content: { width: "480px", position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", parent: document.querySelector(".admin-outer.time.tracker") } }}
            >
                <style>{`
                    .hs-project-scroll { max-height: 50vh; overflow-y: auto; }
                    .hs-project-scroll::-webkit-scrollbar { width: 4px; }
                    .hs-project-scroll::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 4px; }
                    .hs-project-list { display: flex; flex-direction: column; gap: 10px; }
                    .hs-project-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; border-radius: 10px; border: 1px solid #eef0f2; background: #fff; transition: border-color 0.15s, box-shadow 0.15s; }
                    .hs-project-item:hover { border-color: #d6dbff; box-shadow: 0 2px 8px rgba(100,121,248,0.07); }
                    .hs-project-item-info { flex: 1; min-width: 0; }
                    .hs-project-item-name { font-size: 14px; font-weight: 600; color: #282828; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.4; }
                    .hs-project-item-meta { font-size: 12px; color: #9ca3af; margin-top: 3px; line-height: 1.4; }
                    .hs-project-item-synced-name { font-size: 12px; color: #16a34a; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                    .hs-project-item-action { flex-shrink: 0; display: flex; justify-content: flex-end; }
                    .hs-synced-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 600; color: #16a34a; background: #dcfce7; border: 1px solid #bbf7d0; border-radius: 20px; padding: 4px 11px; white-space: nowrap; }
                    .hs-push-btn { background: #6479F8; color: #fff; border: none; border-radius: 6px; padding: 5px 10px; font-size: 11px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background 0.15s; }
                    .hs-push-btn:hover { background: #4f63d4; }
                    .hs-form-label { font-size: 13px; font-weight: 600; color: #454F59; margin-bottom: 5px; display: block; }
                    .hs-form-input { width: 100%; padding: 8px 12px; border: 1px solid #e0e0e0; border-radius: 8px; font-size: 14px; color: #282828; box-sizing: border-box; outline: none; transition: border-color 0.15s; }
                    .hs-form-input:focus { border-color: #6479F8; }
                    .hs-form-row { margin-bottom: 14px; }
                    .hs-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                    .hs-confirm-row { display: flex; align-items: flex-start; gap: 10px; margin: 18px 0 0; padding: 14px; background: #f7f8ff; border-radius: 8px; border: 1px solid #e0e5ff; }
                    .hs-confirm-row input[type=checkbox] { margin-top: 2px; width: 16px; height: 16px; cursor: pointer; flex-shrink: 0; accent-color: #6479F8; }
                    .hs-confirm-row label { font-size: 13px; color: #454F59; cursor: pointer; line-height: 1.5; }
                    .hs-detail-card { background: #f7f8ff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; display: flex; gap: 12px; align-items: center; border: 1px solid #e0e5ff; }
                    .hs-detail-avatar { width: 40px; height: 40px; border-radius: 50%; background: #6479F8; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 700; flex-shrink: 0; }
                    .hs-detail-rows { border-radius: 10px; border: 1px solid #ebebeb; overflow: hidden; }
                    .hs-detail-row { display: flex; padding: 9px 14px; font-size: 13px; border-bottom: 1px solid #f3f4f6; }
                    .hs-detail-row:last-child { border-bottom: none; }
                    .hs-detail-row:nth-child(even) { background: #fafbff; }
                    .hs-detail-row-label { color: #9ca3af; width: 110px; flex-shrink: 0; font-weight: 500; }
                    .hs-detail-row-value { color: #282828; font-weight: 500; }
                    .hs-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
                    .hs-cancel-btn { background: none; border: 1.5px solid #e0e0e0; border-radius: 8px; padding: 8px 18px; font-size: 14px; color: #454F59; cursor: pointer; }
                    .hs-cancel-btn:hover { border-color: #aaa; }
                    .hs-submit-btn { background: #6479F8; color: #fff; border: none; border-radius: 8px; padding: 8px 22px; font-size: 14px; font-weight: 600; cursor: pointer; }
                    .hs-submit-btn:hover:not(:disabled) { background: #4f63d4; }
                    .hs-submit-btn:disabled { opacity: 0.55; cursor: not-allowed; }
                    .hs-empty { text-align: center; color: #c0c4cc; padding: 36px 0; font-size: 14px; }
                    .hs-loading { text-align: center; color: #c0c4cc; padding: 36px 0; font-size: 14px; }
                    .hs-missing-notice { font-size: 12px; color: #e57373; background: #fff5f5; border: 1px solid #fecaca; border-radius: 7px; padding: 8px 12px; margin-bottom: 14px; }
                `}</style>
                <div className="modal">
                    <div className="modal-close-invite outter-modal-main">
                        <a onClick={() => setSyncModalOpen(false)} style={{ cursor: "pointer" }}>
                            <img src={crssrImage} alt="Close" />
                        </a>
                    </div>
                    <div className="modal-client-head">
                        <h2 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M426.6 241.3c-18.7-26.5-45.7-43.2-76.2-48.1v-54.7c13.5-5.8 22.9-19.2 22.9-34.8V92.3C373.3 69 354.3 50 331 50h-11.4c-23.3 0-42.3 19-42.3 42.3v11.4c0 15.6 9.4 29 22.9 34.8v54.7c-22.4 3.4-43.5 12.8-61.5 27.7L117.2 141c1-3.7 1.6-7.6 1.6-11.6 0-25.3-20.5-45.8-45.8-45.8S27.2 104.1 27.2 129.4s20.5 45.8 45.8 45.8c10.6 0 20.4-3.6 28.1-9.6l119.1 78.3c-11.2 20.4-17.4 43.7-17.4 67.8 0 28.3 8.4 55.2 23.9 78l-36.2 36.2c-2.8-.9-5.7-1.4-8.8-1.4-16.5 0-29.9 13.4-29.9 29.9S165.2 484 181.7 484s29.9-13.4 29.9-29.9c0-3.1-.5-6.1-1.4-8.8l35.8-35.8c23.3 16.3 51.1 25.5 80.6 25.5 78.7 0 142.6-63.9 142.6-142.6 0-21.2-4.7-41.7-13.4-60.1l-8.2 8.6-.9-1.6zM325.6 392.5c-55.8 0-101.1-45.3-101.1-101.1s45.3-101.1 101.1-101.1 101.1 45.3 101.1 101.1-45.3 101.1-101.1 101.1z" fill="#ff7a59"/>
                            </svg>
                            HubSpot Projects
                        </h2>
                    </div>
                    <div className="form-field-client-outer">
                        <div className="hs-project-scroll">
                            {hsProjectsLoading ? (
                                <div className="hs-loading">Loading projects from HubSpot…</div>
                            ) : hsProjects.length === 0 ? (
                                <div className="hs-empty">No projects found under this HubSpot company.</div>
                            ) : (
                                <div className="hs-project-list">
                                    {hsProjects.map((proj) => (
                                        <div key={proj.id} className="hs-project-item" title={proj.name || ""}>
                                            <div className="hs-project-item-info">
                                                <div className="hs-project-item-name">
                                                    {proj.name || <em style={{ color: "#c0c4cc", fontWeight: 400 }}>Unnamed project</em>}
                                                </div>
                                                <div className="hs-project-item-meta">
                                                    {[
                                                        proj.start_date && `Start: ${proj.start_date.slice(0, 10)}`,
                                                        proj.end_date && `Due: ${proj.end_date.slice(0, 10)}`,
                                                    ].filter(Boolean).join("  ·  ") || "No dates set"}
                                                </div>
                                                {proj.synced && proj.talenton_project_name && (
                                                    <div className="hs-project-item-synced-name" title={proj.talenton_project_name}>
                                                        ✓ {proj.talenton_project_name}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="hs-project-item-action">
                                                {proj.synced ? (
                                                    <span className="hs-synced-badge">✓ Synced</span>
                                                ) : (
                                                    <button className="hs-push-btn" onClick={() => { setSyncModalOpen(false); openPushModal(proj); }}>
                                                        Push to Talenton
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── Push to Talenton Modal ─────────────────────────────────────────── */}
            <Modal
                className="client-modal"
                isOpen={pushModalOpen}
                onRequestClose={() => setPushModalOpen(false)}
                contentLabel="Push to Talenton"
                style={{ content: { width: "34%", position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", parent: document.querySelector(".admin-outer.time.tracker") } }}
            >
                <div className="modal">
                    <div className="modal-close-invite outter-modal-main">
                        <a onClick={() => setPushModalOpen(false)} style={{ cursor: "pointer" }}>
                            <img src={crssrImage} alt="Close" />
                        </a>
                    </div>
                    <div className="modal-client-head">
                        <h2 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M426.6 241.3c-18.7-26.5-45.7-43.2-76.2-48.1v-54.7c13.5-5.8 22.9-19.2 22.9-34.8V92.3C373.3 69 354.3 50 331 50h-11.4c-23.3 0-42.3 19-42.3 42.3v11.4c0 15.6 9.4 29 22.9 34.8v54.7c-22.4 3.4-43.5 12.8-61.5 27.7L117.2 141c1-3.7 1.6-7.6 1.6-11.6 0-25.3-20.5-45.8-45.8-45.8S27.2 104.1 27.2 129.4s20.5 45.8 45.8 45.8c10.6 0 20.4-3.6 28.1-9.6l119.1 78.3c-11.2 20.4-17.4 43.7-17.4 67.8 0 28.3 8.4 55.2 23.9 78l-36.2 36.2c-2.8-.9-5.7-1.4-8.8-1.4-16.5 0-29.9 13.4-29.9 29.9S165.2 484 181.7 484s29.9-13.4 29.9-29.9c0-3.1-.5-6.1-1.4-8.8l35.8-35.8c23.3 16.3 51.1 25.5 80.6 25.5 78.7 0 142.6-63.9 142.6-142.6 0-21.2-4.7-41.7-13.4-60.1l-8.2 8.6-.9-1.6zM325.6 392.5c-55.8 0-101.1-45.3-101.1-101.1s45.3-101.1 101.1-101.1 101.1 45.3 101.1 101.1-45.3 101.1-101.1 101.1z" fill="#ff7a59"/>
                            </svg>
                            Push to Talenton
                        </h2>
                    </div>
                    <div className="form-field-client-outer">
                        <div className="hs-detail-card">
                            <div className="hs-detail-avatar">
                                {(pushProject?.name || "?")[0]?.toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "#282828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {pushProject?.name || "Unnamed project"}
                                </div>
                                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>HubSpot ID: {pushProject?.id}</div>
                            </div>
                        </div>

                        {(needsForm || editMode) ? (
                            <>
                                {needsForm && (
                                    <div className="hs-missing-notice">
                                        Project name is missing in HubSpot. Please fill in the details below.
                                    </div>
                                )}
                                <div className="hs-form-row">
                                    <label className="hs-form-label">Project Name <span style={{ color: "#e57373" }}>*</span></label>
                                    <input className="hs-form-input" value={pushForm.project_name} onChange={e => setPushForm(f => ({ ...f, project_name: e.target.value }))} placeholder="Enter project name" autoFocus={needsForm} />
                                </div>
                                <div className="hs-form-grid">
                                    <div className="hs-form-row">
                                        <label className="hs-form-label">Start Date</label>
                                        <input className="hs-form-input" type="date" value={pushForm.start_date} onChange={e => setPushForm(f => ({ ...f, start_date: e.target.value }))} />
                                    </div>
                                    <div className="hs-form-row">
                                        <label className="hs-form-label">End Date</label>
                                        <input className="hs-form-input" type="date" value={pushForm.end_date} onChange={e => setPushForm(f => ({ ...f, end_date: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="hs-form-grid">
                                    <div className="hs-form-row">
                                        <label className="hs-form-label">Budget</label>
                                        <input className="hs-form-input" type="number" min="0" value={pushForm.budget} onChange={e => setPushForm(f => ({ ...f, budget: e.target.value }))} placeholder="0" />
                                    </div>
                                    <div className="hs-form-row">
                                        <label className="hs-form-label">Billable</label>
                                        <select className="hs-form-input" value={pushForm.is_billable ? "yes" : "no"} onChange={e => setPushForm(f => ({ ...f, is_billable: e.target.value === "yes" }))}>
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </select>
                                    </div>
                                </div>
                                {editMode && (
                                    <div className="hs-confirm-row">
                                        <input type="checkbox" id="hs-confirm-proj" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                                        <label htmlFor="hs-confirm-proj">
                                            I confirm this project should be created in Talenton and linked to this HubSpot project.
                                        </label>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                                    <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>Project details</span>
                                    <button
                                        onClick={() => { setEditMode(true); setConfirmed(false); }}
                                        style={{ background: "none", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "3px 10px", fontSize: "12px", color: "#6479F8", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}
                                    >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                        Edit
                                    </button>
                                </div>
                                <div className="hs-detail-rows">
                                    {[
                                        { label: "Project Name", value: pushForm.project_name },
                                        { label: "Start Date", value: pushForm.start_date || "—" },
                                        { label: "End Date", value: pushForm.end_date || "—" },
                                        { label: "Budget", value: pushForm.budget ? `$${pushForm.budget}` : "—" },
                                        { label: "Billable", value: pushForm.is_billable ? "Yes" : "No" },
                                    ].map((row) => (
                                        <div key={row.label} className="hs-detail-row">
                                            <span className="hs-detail-row-label">{row.label}</span>
                                            <span className="hs-detail-row-value" style={{ color: row.value === "—" ? "#c0c4cc" : "#282828" }}>{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="hs-confirm-row">
                                    <input type="checkbox" id="hs-confirm-proj" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                                    <label htmlFor="hs-confirm-proj">
                                        I confirm this project should be created in Talenton and linked to this HubSpot project.
                                    </label>
                                </div>
                            </>
                        )}

                        <div className="hs-modal-actions">
                            <button className="hs-cancel-btn" onClick={() => setPushModalOpen(false)}>Cancel</button>
                            <button
                                className="hs-submit-btn"
                                disabled={pushing || (!pushForm.project_name.trim()) || ((!needsForm && !editMode) && !confirmed) || (editMode && !confirmed)}
                                onClick={handlePushSubmit}
                            >
                                {pushing ? "Pushing…" : "Push to Talenton"}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
function Project() {
    const { encryptedId } = useParams();
    if (!isMvpEnv()) return <LegacyProject />;
    const decodedId = decodeURIComponent(String(encryptedId || "").replace(/%2F/g, "/"));
    const id = AES.decrypt(decodedId, "encryptionKey").toString(enc.Utf8);
    return <MvpClientProjectList clientId={id} encryptedId={encryptedId} />;
}

export default Project;